import { useEffect, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/dist/query";

import { useGetTaskQuery } from "fond/api/taskSlice";
import { Task } from "fond/types";

interface UseTaskReturns {
  /**
   * The task object, if it exists.
   */
  task?: Task;
  /**
   * Whether the task is currently running
   */
  isRunning: boolean;
  /**
   * Whether the task failed.
   */
  isError: boolean;
  /**
   * Whether the task succeeded.
   */
  isSuccess: boolean;
}

interface UseTaskProps {
  /**
   * The UUID of the task.
   */
  id: string | null;
  /**
   * The polling period to use when the task is in progress.
   */
  activePollingInterval?: number;
}

/**
 * Hook to interact with a given task based on its id.
 */
export const useTask = ({ id, activePollingInterval }: UseTaskProps): UseTaskReturns => {
  const [pollingInterval, setPollingInterval] = useState<number | undefined>(undefined);
  const { data: task } = useGetTaskQuery(id ? { ID: id } : skipToken, { pollingInterval });
  const isRunning = task ? ["PENDING", "IN_PROGRESS"].includes(task.State) : false;
  const isSuccess = task?.State === "COMPLETE";
  const isError = task?.State === "ERROR";

  // Alter the polling period based on the task state
  useEffect(() => {
    if (isRunning) {
      setPollingInterval(activePollingInterval ?? 10_000);
    } else {
      setPollingInterval(undefined);
    }
  }, [isRunning, setPollingInterval, activePollingInterval]);

  return { task, isRunning, isError, isSuccess };
};
